import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import ConfigureContract from './components/administration/configureContract/ConfigureContract';
import Claims from './components/claims/Claims';
import ManageCommunication from './components/communication/ManageCommunication';
import CreateContract from './components/contract/CreateContract';
import AllActiveCourseTemplateManagement from './components/courses/allActiveCourseTemplates/AllActiveCourseTemplateManagement';
import CreateCourse from './components/courses/courseInstances/CreateCourse';
import EditCourse from './components/courses/courseInstances/EditCourse';
import CourseManagement from './components/courses/CourseManagement';
import CreateCourseTemplate from './components/courses/courseTemplates/CreateCourseTemplate';
import EditCourseTemplate from './components/courses/courseTemplates/EditCourseTemplate';
import ModuleAttendanceDetails from './components/courses/moduleAttendanceDetails/ModuleAttendanceDetails';
import CreateProvision from './components/courses/provisions/CreateProvision';
import EditProvision from './components/courses/provisions/EditProvision';
import AttendanceOutcome from './components/dataSets/attendanceOutcome/AttendanceOutcome';
import CTWReferral from './components/dataSets/ctwReferrals/CTWReferral';
import FinancialRequests from './components/dataSets/financialRequests/FinancialRequests';
import ReferralsCreated from './components/dataSets/referralsCreated/ReferralsCreated';
import Documents from './components/documents/Documents';
import EligibilityGroupManagement from './components/eligibilityGroup/EligibilityGroupManagenment';
import FormBuilder from './components/form/FormBuilder';
import FormManagement from './components/form/FormManagement';
import Home from './components/home/Home';
import NotFound from './components/notFound/NotFound';
import ParticipantAdvancedSearch from './components/participant/advancedSearch/ParticipantAdvancedSearch';
import EditCommunicationGroups from './components/participant/communication/groupCommunication/EditCommunicationGroups';
import GroupEmail from './components/participant/communication/groupCommunication/GroupEmail';
import GroupSMS from './components/participant/communication/groupCommunication/GroupSMS';
import ManageCommunicationGroups from './components/participant/communication/groupCommunication/ManageCommunicationGroups';
import EmailCommunication from './components/participant/communication/singleParticipantCommunication/EmailCommunication';
import LetterCommunication from './components/participant/communication/singleParticipantCommunication/LetterCommunication';
import SMSCommunication from './components/participant/communication/singleParticipantCommunication/SMSCommunication';
import CourseWorkAndEducation from './components/participant/CourseWorkAndEducation';
import CreateParticipant from './components/participant/createParticipant/CreateParticipant';
import CreateParticipantContactCentre from './components/participant/createParticipantContactCentre/CreateParticipantContactCentre';
import EditParticipant from './components/participant/EditParticipant';
import MandatoryForm from './components/participant/form/MandatoryForm';
import ParticipantInformation from './components/participant/participantInformation/ParticipantInformation';
import ParticipantValidation from './components/participant/participantValidation/ParticipantValidation';
import HealthInformation from './components/participant/personal/healthInformation/HealthInformation';
import CreateFinancialRequest from './components/participant/requests/financialRequests/CreateFinancialRequest';
import RequestInformation from './components/participant/requests/RequestInformation';
import ParticipantSearchResults from './components/participant/searchResults/ParticipantSearchResults';
import Questionnaire from './components/questionnaire/Questionnaire';
import QuestionnaireBuilder from './components/questionnaire/QuestionnaireBuilder';
import CreateBusiness from './components/recruitment/businesses/myBusinesses/CreateBusiness';
import EditBusiness from './components/recruitment/businesses/myBusinesses/EditBusiness';
import RecruitmentManagement from './components/recruitment/RecruitmentManagement';
import CreateVacancy from './components/recruitment/vacanciesAndPooling/vacancies/CreateVacancy';
import EditVacancy from './components/recruitment/vacanciesAndPooling/vacancies/EditVacancy';
import VacancySubmissionManagement from './components/recruitment/VacancySubmissionManagement';
import Appointments from './components/scheduling/appointments/Appointments';
import AppointmentSlotsSearch from './components/scheduling/appointmentSlotSearch/AppointmentSlotsSearch';
import CourseCalendar from './components/scheduling/calendarForCourseModules/CourseCalendar';
import ParticipantCalendar from './components/scheduling/calendarForParticipants/ParticipantCalendar';
import SchedulingAssistant from './components/scheduling/calendarForSchedulingAssistant/SchedulingAssistant';
import UserCalendar from './components/scheduling/calendarForUsers/UserCalendar';
import CreateService from './components/service/CreateService';
import TBC from './components/tbc/TBC';
import CreateTeam from './components/team/CreateTeam';
import Timeline from './components/timeline/Timeline';
import CreateUser from './components/user/createUser/CreateUser';
import ManageUsers from './components/user/usersManagement/ManageUsers';
import { msalInstance } from './masl/authConfig';
import App from './App';
import { store } from './store';

import './index.css';

const root = ReactDOM.createRoot(document.getElementById('root'));
const router = createBrowserRouter([
    {
        path: '/',
        element: <App instance={msalInstance} />,
        children: [
            { index: true, element: <Home /> },
            {
                path: 'all_active_course_templates',
                element: <AllActiveCourseTemplateManagement />
            },
            { path: 'appointments', element: <Appointments /> },
            { path: 'appointment_slots_search', element: <AppointmentSlotsSearch /> },
            { path: 'attendance_outcome', element: <AttendanceOutcome /> },
            { path: 'build_form', element: <FormBuilder /> },
            { path: 'build_questionnaire', element: <QuestionnaireBuilder /> },
            { path: 'claims', element: <Claims /> },
            { path: 'configure_contract', element: <ConfigureContract /> },
            { path: 'contact_centre', element: <CreateParticipantContactCentre /> },
            { path: 'course_calendar', element: <CourseCalendar /> },
            { path: 'course_work_and_education', element: <CourseWorkAndEducation /> },
            { path: 'course_management', element: <CourseManagement /> },
            { path: 'create_business', element: <CreateBusiness /> },
            { path: 'create_contract', element: <CreateContract /> },
            { path: 'create_course', element: <CreateCourse /> },
            { path: 'create_course_template', element: <CreateCourseTemplate /> },
            { path: 'ctw_referral', element: <CTWReferral /> },
            { path: 'manage_eligibility_groups', element: <EligibilityGroupManagement /> },
            { path: 'create_financial_request', element: <CreateFinancialRequest /> },
            { path: 'create_participant', element: <CreateParticipant /> },
            { path: 'create_provision', element: <CreateProvision /> },
            { path: 'create_service', element: <CreateService /> },
            { path: 'create_team', element: <CreateTeam /> },
            { path: 'create_vacancy', element: <CreateVacancy /> },
            { path: 'create_user', element: <CreateUser /> },
            { path: 'documents', element: <Documents /> },
            { path: 'edit_business', element: <EditBusiness /> },
            { path: 'edit_communication_groups', element: <EditCommunicationGroups /> },
            { path: 'edit_course', element: <EditCourse /> },
            { path: 'edit_course_template', element: <EditCourseTemplate /> },
            { path: 'edit_participant', element: <EditParticipant /> },
            { path: 'edit_provision', element: <EditProvision /> },
            { path: 'edit_vacancy', element: <EditVacancy /> },
            { path: 'email_communication', element: <EmailCommunication /> },
            { path: 'financial_information', element: <RequestInformation /> },
            { path: 'financial_requests', element: <FinancialRequests /> },
            { path: 'group_sms', element: <GroupSMS /> },
            { path: 'group_email', element: <GroupEmail /> },
            { path: 'health_information', element: <HealthInformation /> },
            { path: 'home', element: <Home /> },
            { path: 'letter_communication', element: <LetterCommunication /> },
            { path: 'manage_communication', element: <ManageCommunication /> },
            { path: 'manage_form', element: <FormManagement /> },
            { path: 'manage_groups', element: <ManageCommunicationGroups /> },
            { path: 'manage_questionnaire', element: <Questionnaire /> },
            { path: 'manage_users', element: <ManageUsers /> },
            { path: 'mandatory_form', element: <MandatoryForm /> },
            { path: 'module_attendance_details', element: <ModuleAttendanceDetails /> },
            { path: 'participant_calendar', element: <ParticipantCalendar /> },
            { path: 'participant_information', element: <ParticipantInformation /> },
            { path: 'search_participant', element: <ParticipantAdvancedSearch /> },
            { path: 'participant_search_results', element: <ParticipantSearchResults /> },
            { path: 'participant_validation', element: <ParticipantValidation /> },
            { path: 'referrals_created', element: <ReferralsCreated /> },
            { path: 'recruitment_management', element: <RecruitmentManagement /> },
            { path: 'scheduling_assistant', element: <SchedulingAssistant /> },
            { path: 'sms_communication', element: <SMSCommunication /> },
            { path: 'tbc', element: <TBC /> },
            { path: 'timeline', element: <Timeline /> },
            { path: 'user_calendar', element: <UserCalendar /> },
            { path: 'vacancy_management', element: <VacancySubmissionManagement /> },
            { path: '*', element: <NotFound /> }
        ]
    }
]);
root.render(
    <Provider store={store}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <RouterProvider router={router} />
        </LocalizationProvider>
    </Provider>
);
